import React, { useEffect, useMemo } from "react";
import { RouteComponentProps } from "react-router-dom";
import {
  ActionLink,
  CurrencyFormatters,
  TravelWalletSelectionWorkflow,
  getRestrictedATCText,
  getRestrictedUserTCTexts,
  filterOutSpendAndGetOffers,
  isSpendAndGetOffer,
  getCreditActivityGroups,
  formatPartialHistoryValidFromDate,
  getUpcomingCreditActivityItems,
} from "halifax";
import { ENGAGED_OFFER_CTA, Funnel, SelectedTravelOfferScreen } from "redmond";
import { CreditActivityDialog } from "@capone/portal";
import { CreditActivityDialog as VesperCreditActivityDialog } from "@vesper/portal";
import { CreditActivityDialog as CorpCreditActivityDialog } from "@capone-corporate/portal";
import { TravelWalletSelectionConnectorProps } from "./container";
import {
  AVAILABLE,
  C1_AUTO_SELECT_CARD_AT_CHECKOUT,
  CORP_FTB_OFFER_ADDITIONAL_PLACEMENTS,
  getExperimentVariant,
  useExperiments,
} from "../../../../context/experiments";
import {
  APPLY_TRAVEL_CREDIT_TITLE,
  APPLY_TRAVEL_OFFER_TITLE,
  CREDIT_BALANCE_DESCRIPTION,
  CREDIT_HISTORY_BALANCE_TEXT,
  CREDIT_HISTORY_MODAL_LIST_HEADER,
  CREDIT_HISTORY_MODAL_SUBHEADER,
  getSelectedCreditDescription,
  getSelectedOfferDescription,
  TRAVEL_CREDIT_AND_OFFER_TOTAL_PAYMENT_TEXT,
  TRAVEL_CREDIT_SUBTITLE,
  TRAVEL_CREDIT_TOTAL_PAYMENT_TEXT,
  TRAVEL_OFFER_SUBTITLE,
  TRAVEL_OFFER_TOTAL_PAYMENT_TEXT,
  VIEW_CREDIT_HISTORY_CTA,
} from "./textConstants";
import { ClientContext } from "../../../../App";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import {
  getConfigTenant,
  isCorpTenant,
  isVesperTenant,
  hasIneligibleTravelOffer,
} from "@capone/common";

const DO_NOT_APPLY_REWARDS_KEY = "do-not-apply-rewards";

export interface ITravelWalletSelectionProps
  extends TravelWalletSelectionConnectorProps,
    RouteComponentProps {
  className?: string;
  isMobile?: boolean;
  disabled?: boolean;
  isAnnualTravelCreditsExperiment?: boolean;
  isCreditAndOfferStackingExperimentV1?: boolean;
  isTravelSale?: boolean;
}

const getCreditActivityDialogByTenant = () => {
  const tenant = getConfigTenant();
  if (isCorpTenant(tenant)) return CorpCreditActivityDialog;
  if (isVesperTenant(tenant)) return VesperCreditActivityDialog;
  return CreditActivityDialog;
};

export const TravelWalletSelection = (props: ITravelWalletSelectionProps) => {
  const {
    offers,
    offerToApply,
    setOfferToApply,
    credits,
    creditBreakdown,
    creditsToApply,
    setCreditToApply,
    disabled,
    isTravelCreditPaymentOnly,
    isTravelWalletOfferPaymentOnly,
    isStackedTravelWalletPaymentOnly,
    isMobile,
    creditsHistoryTransaction,
    partialHistoryValidFromTime,
    bestOverallOffer,
    selectedPaymentMethodId,
    selectedRewardsPaymentAccountId,
    setSelectedRewardsPaymentTotal,
    resetPaymentCardSelectedAccounts,
    showRequoteSuccessBanner,
    isRequotePending,
    rewardsAccounts,
    isAnnualTravelCreditsExperiment,
    isCreditAndOfferStackingExperimentV1,
    maxApplicableTravelWalletCredit,
    isTravelSale,
    ineligibleCreditsBreakdown,
    bookingTotal,
    allWalletOffers,
  } = props;

  const firstName =
    React.useContext(ClientContext).sessionInfo?.userInfo?.firstName || "";

  const filteredOffers = useMemo(
    () => (offers ? filterOutSpendAndGetOffers(offers) : undefined),
    [offers],
  );

  const filteredBestOverallOffer = useMemo(
    () =>
      bestOverallOffer && isSpendAndGetOffer(bestOverallOffer.descriptions)
        ? undefined
        : bestOverallOffer,
    [bestOverallOffer],
  );

  const expState = useExperiments();
  const isFtbEnabled =
    getExperimentVariant(
      expState.experiments,
      CORP_FTB_OFFER_ADDITIONAL_PLACEMENTS,
    ) === AVAILABLE;
  const autoSelectExperimentOn =
    getExperimentVariant(
      expState.experiments,
      C1_AUTO_SELECT_CARD_AT_CHECKOUT,
    ) === AVAILABLE;

  const hasIneligibleFTB = useMemo(
    () =>
      isFtbEnabled &&
      isCorpTenant(getConfigTenant()) &&
      hasIneligibleTravelOffer(allWalletOffers, filteredOffers, Funnel.Lodging),
    [isFtbEnabled, filteredOffers, allWalletOffers],
  );

  useEffect(() => {
    if (credits) {
      setCreditToApply(credits);
    }
  }, [credits]);

  useEffect(() => {
    if (filteredBestOverallOffer) {
      setOfferToApply(filteredBestOverallOffer);
    }
  }, [filteredBestOverallOffer]);

  useEffect(() => {
    const hasRealRewardsSelection =
        selectedRewardsPaymentAccountId &&
        selectedRewardsPaymentAccountId !== DO_NOT_APPLY_REWARDS_KEY;

    if (selectedPaymentMethodId || hasRealRewardsSelection) {
        // Don't clear the card across a reshop or connection-retry (CT1261T-419)
        // re-quote — guard both the pending re-quote (isRequotePending, banner not
        // set yet) and the post-success window (showRequoteSuccessBanner); the
        // wallet response can land in either.
        if (
          !autoSelectExperimentOn &&
          !isRequotePending &&
          !showRequoteSuccessBanner
        ) {
          resetPaymentCardSelectedAccounts();
        }
        setSelectedRewardsPaymentTotal("", null, null, null);
      }
      // The requote flags are read above but intentionally not deps: only an
      // offer/credit change should run this, not a flag flip (e.g. dismissal).
  }, [offerToApply, creditsToApply, autoSelectExperimentOn]);
        

  if (!filteredOffers && !credits && !hasIneligibleFTB) {
    return null;
  }

  const ResolvedCreditActivityDialog = getCreditActivityDialogByTenant();

  return (
    <TravelWalletSelectionWorkflow
      isMobile={isMobile}
      offersAvailable={filteredOffers || []}
      creditsAvailable={credits}
      offerToApply={offerToApply}
      creditsToApply={creditsToApply}
      setOfferToApply={setOfferToApply}
      setCreditToApply={setCreditToApply}
      titles={{
        creditTitle: APPLY_TRAVEL_CREDIT_TITLE,
        creditSubtitle: TRAVEL_CREDIT_SUBTITLE,
        offersTitle: APPLY_TRAVEL_OFFER_TITLE,
        offersSubtitle: TRAVEL_OFFER_SUBTITLE,
      }}
      getCreditDescription={getSelectedCreditDescription}
      getOfferDescription={getSelectedOfferDescription}
      disabled={disabled}
      totalCoverageTexts={{
        creditsCoverTotalText: isTravelCreditPaymentOnly
          ? TRAVEL_CREDIT_TOTAL_PAYMENT_TEXT
          : undefined,
        offerCoversTotalText: isTravelWalletOfferPaymentOnly
          ? TRAVEL_OFFER_TOTAL_PAYMENT_TEXT
          : undefined,
        travelWalletCoverTotalText: isStackedTravelWalletPaymentOnly
          ? TRAVEL_CREDIT_AND_OFFER_TOTAL_PAYMENT_TEXT
          : undefined,
      }}
      isTravelWalletCreditPaymentOnly={isTravelCreditPaymentOnly}
      isTravelWalletOfferPaymentOnly={isTravelWalletOfferPaymentOnly}
      maxApplicableCreditAmount={maxApplicableTravelWalletCredit}
      travelCreditHistoryProps={
        creditsHistoryTransaction?.length
          ? {
              modalContentProps: {
                header: CREDIT_BALANCE_DESCRIPTION(firstName),
                balanceText: CREDIT_HISTORY_BALANCE_TEXT(
                  CurrencyFormatters.getSymbol(credits?.amount.currency),
                  CurrencyFormatters.get(credits?.amount.currency).format(
                    (credits?.amount?.amount && credits?.amount?.amount * -1) ||
                      0,
                  ),
                ),
                subHeader: CREDIT_HISTORY_MODAL_SUBHEADER,
                creditListHeader: CREDIT_HISTORY_MODAL_LIST_HEADER,
              },
              viewHistoryCTAText: VIEW_CREDIT_HISTORY_CTA,
              creditTransactionsHistory: creditsHistoryTransaction,
              credit: credits,
              creditBreakdown,
              rewardsAccounts,
            }
          : undefined
      }
      creditActivityDialogSlot={
        creditsHistoryTransaction?.length ? (
          <ResolvedCreditActivityDialog
            trigger={<ActionLink content={VIEW_CREDIT_HISTORY_CTA} />}
            groups={getCreditActivityGroups(
              creditsHistoryTransaction,
              creditBreakdown,
            )}
            upcomingItems={getUpcomingCreditActivityItems(creditBreakdown)}
            partialHistoryValidFromDate={formatPartialHistoryValidFromDate(
              partialHistoryValidFromTime,
            )}
          />
        ) : undefined
      }
      isAnnualTravelCreditsExperiment={isAnnualTravelCreditsExperiment}
      restrictedATCText={
        getRestrictedATCText(rewardsAccounts, creditBreakdown) ||
        getRestrictedUserTCTexts(rewardsAccounts, creditBreakdown)?.[0]
      }
      isCreditAndOfferStackingExperimentV1={
        isCreditAndOfferStackingExperimentV1
      }
      isTravelSale={isTravelSale}
      onClickOfferTooltip={(offer) => {
        trackEvent({
          eventName: ENGAGED_OFFER_CTA,
          properties: {
            location: SelectedTravelOfferScreen.HOTEL_CHECKOUT,
            entry_type: "tooltip",
            funnel: offer.funnels.join(","),
          },
          encryptedProperties: [
            offer.trackingPropertiesV2?.encryptedProperties ?? "",
          ],
        });
      }}
      ineligibleCreditsBreakdown={ineligibleCreditsBreakdown}
      bookingTotal={bookingTotal}
      allWalletOffers={allWalletOffers}
      currentFunnel={Funnel.Lodging}
    />
  );
};
